<div class="stepper-dialog relative" *transloco="let t">
	<button class="absolute right-2 top-2 z-10" mat-icon-button (click)="onCancel()" [attr.aria-label]="t('commonActions.close')">
		<mat-icon>close</mat-icon>
	</button>
	<h2 mat-dialog-title cdkDrag cdkDragHandle cdkDragRootElement=".cdk-overlay-pane">{{ t('billing.addAllocation') }}</h2>

	<mat-dialog-content>
		<mat-stepper #stepper [selectedIndex]="currentStep" linear>
			<!-- Schritt 1: tenantShare -->
			<mat-step [completed]="step1Valid">
				<ng-template matStepLabel>{{ t('billing.costGroupCategory') }}</ng-template>
				<div class="step-content">
					<p>{{ t('billing.costGroupCategoryExplanation') }}</p>

					<mat-radio-group [(ngModel)]="formData.costGroupCategory" [disabled]="!!this.costAllocationIdToEdit" name="tenantShare">
						<div class="flex flex-col">
							<mat-radio-button value="HEAT_ENERGY">
								{{ t('enum.HEAT_ENERGY') }}
							</mat-radio-button>
						</div>
						<div class="flex flex-col pt-4">
							<mat-radio-button value="HEATING_OPERATION">
								{{ t('enum.HEATING_OPERATION') }}
							</mat-radio-button>
						</div>
						<div class="flex flex-col pt-4">
							<mat-radio-button value="ANCILLARY_SERVICES">
								{{ t('enum.ANCILLARY_SERVICES') }}
							</mat-radio-button>
						</div>
					</mat-radio-group>
				</div>
			</mat-step>

			<!-- Schritt 2: Pauschalisierung -->
			<mat-step [completed]="step2Valid">
				<ng-template matStepLabel>{{ t('billing.shareTypeDetailTitle') }}</ng-template>

				<div class="step-content flex flex-row justify-start items-center">
					<div>
						<p>{{ t('billing.shareTypeDetail') }}</p>
						<div>
							<div class="select-container flex flex-row justify-start items-center w-full gap-2">
								<mat-form-field appearance="outline" class="w-full">
									<mat-label>{{ t('billing.constantTenantShareSelect') }}</mat-label>
									<mat-select
										[(ngModel)]="formData.tenantShare"
										name="selectedOption"
										panelClass="custom-select-options"
										(selectionChange)="formData.ordinaryShare = undefined; formData.shareType = CostAllocationDataType.SHARE"
										[compareWith]="compareObjects">
										<mat-select-trigger>
											{{ getTenantShareName() }}
											@if (formData.tenantShare) {
												<mat-icon
													class="mat-select-clear-icon"
													[matTooltip]="t('commonActions.deleteSelection')"
													(click)="$event.stopPropagation(); deleteShareAndFollowing()">
													clear
												</mat-icon>
											}
										</mat-select-trigger>
										@for (tenantShare of tenantShares; track tenantShare) {
											<mat-option [value]="tenantShare">
												<div class="flex flex-row pb-2">
													<div class="font-bold">
														{{ tenantShare.name }}
													</div>
													<div class="pl-1">
														{{ printAddInfo(tenantShare) }}
													</div>
												</div>
												<div class="flex flex-col">
													<div class="italic">{{ t('billing.description') }}: {{ tenantShare.description }}</div>
													<div class="flex flex-row items-start justify-between text-sm">
														@if (tenantShare.type) {
															<div>{{ t('billing.type') }} : {{ t('tenantShareType.' + tenantShare.type) }}</div>
														}
														@if (tenantShare.validFrom && tenantShare.validTo) {
															<div class="pl-6">
																{{ t('billing.dateRange') }} : {{ tenantShare.validFrom }} - {{ tenantShare.validTo }}
															</div>
														}
														@if (this.tenantShareTypeMeta[tenantShare.type]?.unit) {
															<div class="pl-6">
																{{ t('billing.unit') }} : {{ t('enum.' + this.tenantShareTypeMeta[tenantShare.type].unit) }}
															</div>
														}
													</div>
												</div>
											</mat-option>
										}
									</mat-select>
								</mat-form-field>
							</div>
						</div>
					</div>
					<div class="pl-4">
						<p>{{ t('billing.ordinaryTypeDetail') }}</p>
						<div class="select-container flex flex-row justify-start items-center w-full gap-2">
							<mat-form-field appearance="outline" class="w-full">
								<mat-label>{{ t('billing.dynamicTenantShareSelect') }}</mat-label>
								<mat-select
									[(ngModel)]="formData.ordinaryShare"
									name="selectedOption"
									(selectionChange)="formData.tenantShare = undefined; formData.shareType = CostAllocationDataType.ORDINARY"
									[compareWith]="compareOrdinaryAllocations">
									<mat-select-trigger>
										{{ translatedOrdinaryAllocationTypes.get(formData.ordinaryShare) }}
										@if (formData.ordinaryShare) {
											<mat-icon
												class="mat-select-clear-icon"
												[matTooltip]="t('commonActions.deleteSelection')"
												(click)="$event.stopPropagation(); deleteShareAndFollowing()">
												clear
											</mat-icon>
										}
									</mat-select-trigger>
									@for (ordinaryAllocationType of translatedOrdinaryAllocationTypes.keys(); track ordinaryAllocationType) {
										<mat-option [value]="ordinaryAllocationType">
											{{ translatedOrdinaryAllocationTypes.get(ordinaryAllocationType) }}
										</mat-option>
									}
								</mat-select>
							</mat-form-field>
						</div>
					</div>
				</div>
			</mat-step>

			<mat-step [completed]="step3Valid">
				<ng-template matStepLabel>{{ t('billing.addSelection') }}</ng-template>

				<div class="step-content">
					<p>{{ t('billing.addSelectionQ') }}</p>
					<div class="select-container flex flex-row justify-start items-center w-full gap-2">
						<mat-form-field appearance="outline" class="flex-grow">
							<mat-label>{{ t('billing.addSelectionSelect') }}</mat-label>
							<mat-select
								[(ngModel)]="formData.tenantSelection"
								name="selectedFlatrate"
								panelClass="custom-select-options"
								[compareWith]="compareObjects">
								<mat-select-trigger>
									{{ getTenantSelectionName() }}
									@if (formData.tenantSelection) {
										<mat-icon
											class="mat-select-clear-icon"
											[matTooltip]="t('commonActions.deleteSelection')"
											(click)="$event.stopPropagation(); formData.tenantSelection = undefined">
											clear
										</mat-icon>
									}
								</mat-select-trigger>
								@for (exemption of tenantSelections; track exemption) {
									<mat-option [value]="exemption">
										<div class="flex flex-row pb-2">
											<div class="font-bold">
												{{ exemption.name }}
											</div>
											<div class="pl-1">
												{{ printAddInfo(exemption) }}
											</div>
										</div>
										<div class="flex flex-col">
											@if (exemption.description) {
												<div class="italic">{{ t('billing.description') }}:{{ exemption.description }}</div>
											}
											<div class="flex flex-row items-start justify-between text-sm">
												@if (exemption.validFrom && exemption.validTo) {
													<div class="pl-1">{{ t('billing.dateRange') }} : {{ exemption.validFrom }} - {{ exemption.validTo }}</div>
												}
											</div>
										</div>
									</mat-option>
								}
							</mat-select>
						</mat-form-field>
					</div>
				</div>
			</mat-step>
			<mat-step>
				<ng-template matStepLabel>{{ t('common.result') }}</ng-template>

				<div class="step-content">
					<p>{{ t('billing.result') }}</p>
					<mat-form-field appearance="outline" class="w-[50%]">
						<mat-label>{{ t('billing.allocationName') }}</mat-label>
						<input [(ngModel)]="formData.name" matInput placeholder="{{ t('billing.allocationName') }}" required />
					</mat-form-field>

					<div class="summary-item">
						<strong>{{ formData.shareType === CostAllocationDataType.ORDINARY ? t('billing.dynamic') : t('billing.constant') }}</strong>
						<span class="summary-value">
							@if (formData.shareType === CostAllocationDataType.ORDINARY) {
								{{ formData.ordinaryShare | translate: 'billing.ordinaryAllocationType' : t }}
							} @else if (formData.shareType === CostAllocationDataType.SHARE) {
								{{ getTenantShareName() }}
							}
						</span>
					</div>

					<div class="summary-item">
						<strong>{{ t('billing.exemption') }}</strong>
						<span class="summary-value">
							<div class="flex flex-col justify-start items-center">
								<div>
									{{
										formData.tenantSelection?.name
											? t('billing.parameterValue.EXEMPTION') + ': ' + formData.tenantSelection?.name
											: t('common.nA')
									}}
								</div>
								<div>
									{{ formData.tenantSelection?.printname ? t('common.printName') + ': ' + formData.tenantSelection?.printname : '' }}
								</div>
							</div>
						</span>
					</div>

					@if (isConfigurationComplete()) {
						<mat-card class="summary-card">
							<mat-card-content>
								<mat-icon color="primary">check_circle</mat-icon>
								{{ t('billing.configurationComplete') }}
							</mat-card-content>
						</mat-card>
					}
				</div>
			</mat-step>
		</mat-stepper>
	</mat-dialog-content>

	<mat-dialog-actions class="flex flex-row items-center justify-end">
		<div>
			<button mat-raised-button class="m-2" (click)="onCancel()">
				<mat-icon>cancel</mat-icon>
				{{ t('commonActions.cancel') }}
			</button>
			@if (currentStep > 0) {
				<button mat-raised-button class="m-2" (click)="previousStep()">
					<mat-icon>arrow_back</mat-icon>
					{{ t('permissions.previous') }}
				</button>
			}
			@if (currentStep < 4) {
				<button mat-raised-button color="primary" class="m-2" [disabled]="!canProceed()" (click)="nextStep()">
					<mat-icon>arrow_forward</mat-icon>
					{{ t('permissions.next') }}
				</button>
			}
			@if (currentStep === 4) {
				<button mat-raised-button color="primary" class="m-2" [disabled]="!isConfigurationComplete()" (click)="onComplete()">
					<mat-icon>check</mat-icon>
					{{ t('commonActions.complete') }}
				</button>
			}
		</div>
	</mat-dialog-actions>
</div>
